import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, CheckCircle2, ExternalLink, Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Carte "Connecter Telegram" — affichée dans /settings.
 * Permet à chaque user (Fred, Sandrine, salariés) de lier son Telegram
 * pour recevoir les tâches assignées en notification push.
 */
export default function TelegramConnectionCard() {
  const [status, setStatus] = useState({ connected: false, loading: true });
  const [generatingLink, setGeneratingLink] = useState(false);
  const [deepLink, setDeepLink] = useState(null);
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get('/telegram-perso/status');
      setStatus({ ...data, loading: false });
    } catch (e) {
      setStatus({ connected: false, loading: false });
    }
  };

  useEffect(() => {
    refresh();
    // Re-check toutes les 5s tant que pas connecté (l'user vient peut-être de cliquer le lien)
    const itv = setInterval(() => {
      if (!status.connected) refresh();
    }, 5000);
    return () => clearInterval(itv);
  }, [status.connected]);

  const generate = async () => {
    setGeneratingLink(true);
    try {
      const { data } = await api.post('/telegram-perso/connect');
      setDeepLink(data);
    } catch (e) {
      toast.error("Impossible de générer le lien Telegram");
    } finally {
      setGeneratingLink(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post('/telegram-perso/test-send');
      toast.success("Message de test envoyé sur ton Telegram 📲");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Délier Telegram ? Tu ne recevras plus de notifs de tâches.")) return;
    try {
      await api.post('/telegram-perso/disconnect');
      setStatus({ connected: false, loading: false });
      setDeepLink(null);
      toast.success("Telegram délié");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <Card data-testid="telegram-connection-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <svg viewBox="0 0 240 240" className="w-6 h-6">
                <defs>
                  <linearGradient id="tg-grad" x1=".667" y1=".167" x2=".417" y2=".75">
                    <stop offset="0" stopColor="#37aee2" />
                    <stop offset="1" stopColor="#1e96c8" />
                  </linearGradient>
                </defs>
                <circle cx="120" cy="120" r="120" fill="url(#tg-grad)" />
                <path
                  fill="#fff"
                  d="M81.229 128.772l14.237 39.406s1.781 3.687 3.687 3.687c1.906 0 30.255-29.479 30.255-29.479l31.525-60.886-79.703 37.385z"
                  opacity=".9"
                />
                <path
                  fill="#fff"
                  d="M100.106 138.878l-2.772 29.448s-1.156 9 7.844 0c9-9 17.594-15.938 17.594-15.938"
                  opacity=".9"
                />
                <path
                  fill="#fff"
                  d="M81.486 130.178l-29.359-9.564s-3.51-1.422-2.385-4.65c.232-.666.7-1.232 2.1-2.205 6.495-4.526 120.2-45.36 120.2-45.36s3.218-1.084 5.115-.366c.869.328 1.426 1.026 1.59 1.876.166.85.066 1.74-.06 2.625-.135.948-.342 2.077-.55 3.21-1.985 10.6-21.05 121.78-21.05 121.78s-1.083 4.262-4.962 4.41c-1.412.05-3.142-.235-5.205-2.014-7.659-6.59-34.15-24.394-40.005-28.32-.33-.222-.42-.504-.49-.778-.14-.402.255-.9.255-.9s46.13-41.005 47.36-45.31c.094-.337-.27-.504-.755-.357-3.087.937-58.045 35.582-64.08 39.385-.358.225-1.4.225-1.4.225"
                />
              </svg>
              Telegram
              {status.connected && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="badge-telegram-connected">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Connecté
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              Reçois tes tâches assignées en notification push (Fred ou Valentine).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : status.connected ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm bg-emerald-50 border border-emerald-200 rounded-md p-3">
              <div>
                <p className="font-medium text-emerald-900">Telegram lié ✅</p>
                {status.username && <p className="text-xs text-emerald-700">@{status.username}</p>}
                {status.linked_at && (
                  <p className="text-xs text-emerald-700">
                    Lié le {new Date(status.linked_at).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={sendTest} disabled={sending} variant="outline" size="sm" data-testid="btn-test-telegram">
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer un test
              </Button>
              <Button onClick={disconnect} variant="outline" size="sm" className="text-red-600 hover:text-red-700" data-testid="btn-disconnect-telegram">
                <X className="h-4 w-4 mr-2" />
                Délier
              </Button>
            </div>
          </div>
        ) : deepLink ? (
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm">
              <p className="font-medium text-blue-900 mb-2">📱 Connecte ton Telegram</p>
              <ol className="text-blue-800 list-decimal pl-4 space-y-1 text-xs">
                <li>Ouvre ce lien sur ton téléphone (où Telegram est installé)</li>
                <li>Clique sur <strong>« Démarrer »</strong> dans la conversation</li>
                <li>Tu recevras un message de confirmation ✅</li>
              </ol>
            </div>
            <a
              href={deepLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="link-telegram-deeplink"
            >
              <Button className="w-full bg-[#37aee2] hover:bg-[#1e96c8]">
                <ExternalLink className="h-4 w-4 mr-2" />
                Ouvrir sur Telegram (@{deepLink.bot_username})
              </Button>
            </a>
            <p className="text-xs text-slate-500 text-center">
              Lien valable 15 minutes. La connexion sera détectée automatiquement.
            </p>
            <code className="block text-xs bg-slate-100 p-2 rounded break-all" data-testid="telegram-url">
              {deepLink.url}
            </code>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              <p>Avantages :</p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
                <li>📲 Notifications push instantanées des tâches assignées</li>
                <li>✅ Boutons « Reçu / En cours / Fait » directement dans Telegram</li>
                <li>🚜 Pratique sur le terrain ou en tracteur</li>
              </ul>
            </div>
            <Button
              onClick={generate}
              disabled={generatingLink}
              className="w-full bg-[#37aee2] hover:bg-[#1e96c8]"
              data-testid="btn-connect-telegram"
            >
              {generatingLink ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Connecter Telegram
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
